import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, Plus, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useWorkspace } from "../lib/WorkspaceContext";
import { useAuth } from "../lib/AuthContext";
import RoleBadge from "../components/RoleBadge";

export default function WorkspacesPage() {
  const { t } = useTranslation("workspaces");
  const { user } = useAuth();
  const { workspaces, current, currentId, switchTo, refresh } = useWorkspace();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create workspace
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  // Invite
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);

  const myRole = current?.role || "viewer";
  const canManage = myRole === "owner" || myRole === "admin";
  const canInviteAdmin = myRole === "owner";

  const fetchWorkspaceDetails = async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.get(`/workspaces/${currentId}/members`),
        canManage
          ? api.get(`/workspaces/${currentId}/invitations`).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setMembers(m.data || []);
      setInvitations(i.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toasts.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, canManage]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/workspaces", { name: newName.trim() });
      toast.success(t("toasts.create_success", { name: res.data.name }));
      setNewName("");
      await refresh();
      switchTo(res.data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toasts.create_error"));
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await api.post(`/workspaces/${currentId}/invitations`, {
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
      });
      if (res.data.auto_accepted) {
        toast.success(t("toasts.member_added", { email: res.data.email, role: res.data.role }));
      } else {
        toast.success(t("toasts.invite_sent", { email: res.data.email }));
      }
      setInviteEmail("");
      await fetchWorkspaceDetails();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toasts.invite_error"));
    } finally {
      setInviting(false);
    }
  };

  const handleChangeRole = async (uid, role) => {
    try {
      await api.patch(`/workspaces/${currentId}/members/${uid}`, { role });
      toast.success(t("toasts.role_updated", { role }));
      await fetchWorkspaceDetails();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toasts.role_change_error"));
    }
  };

  const handleRemoveMember = async (uid) => {
    if (!window.confirm(t("members.remove_confirm"))) return;
    try {
      await api.delete(`/workspaces/${currentId}/members/${uid}`);
      toast.success(t("toasts.member_removed"));
      await fetchWorkspaceDetails();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toasts.remove_error"));
    }
  };

  const handleRevoke = async (invId) => {
    try {
      await api.delete(`/workspaces/${currentId}/invitations/${invId}`);
      toast.success(t("toasts.invitation_revoked"));
      await fetchWorkspaceDetails();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toasts.revoke_error"));
    }
  };

  return (
    <div className="space-y-10" data-testid="workspaces-page">
      <header>
        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-1">{t("kicker")}</p>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">{t("list.title")}</h1>
        <p className="text-zinc-600 mt-2 max-w-2xl">
          {t("page_subtitle")}
        </p>
      </header>

      {/* Workspace switcher + create */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-zinc-200 bg-white p-6">
          <h2 className="text-base font-semibold text-zinc-900 mb-3">{t("list.title")}</h2>
          <ul className="space-y-2" data-testid="workspaces-list">
            {workspaces.map((w) => {
              const active = w.id === currentId;
              return (
                <li key={w.id}>
                  <button
                    onClick={() => switchTo(w.id)}
                    data-testid={`workspace-switch-${w.id}`}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition ${
                      active
                        ? "border-zinc-900 bg-zinc-950 text-white"
                        : "border-zinc-200 bg-white hover:border-zinc-300"
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Users size={15} strokeWidth={1.75} className={active ? "text-white" : "text-zinc-500"} />
                      <span className="truncate font-medium">{w.name}</span>
                      {w.is_personal && (
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${active ? "bg-white/15" : "bg-zinc-100 text-zinc-500"}`}>
                          {t("list.personal_badge")}
                        </span>
                      )}
                    </span>
                    <RoleBadge role={w.role} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-white p-6">
          <h2 className="text-base font-semibold text-zinc-900 mb-3">{t("create.title")}</h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <input
              type="text"
              placeholder={t("create.name_placeholder")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={80}
              data-testid="new-workspace-name"
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
            />
            <button
              type="submit"
              disabled={creating || !newName.trim()}
              data-testid="create-workspace-button"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-zinc-950 text-white text-sm font-medium disabled:opacity-50"
            >
              <Plus size={15} /> {creating ? t("create.submitting") : t("create.submit")}
            </button>
          </form>
          <p className="text-xs text-zinc-500 mt-3">
            {t("create.hint")}
          </p>
        </div>
      </section>

      {/* Members */}
      <section className="rounded-xl border border-zinc-200 bg-white">
        <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-zinc-900">
              {t("members.title", { name: current?.name || "—" })}
            </h2>
            <p className="text-sm text-zinc-500 mt-1">{t("members.your_role")} <RoleBadge role={myRole} /></p>
          </div>
        </div>
        <ul className="divide-y divide-zinc-200" data-testid="members-list">
          {loading && <li className="px-6 py-4 text-sm text-zinc-500">{t("common:actions.loading")}</li>}
          {!loading && members.map((m) => {
            const isSelf = m.user_id === user?.id;
            const isOwner = m.role === "owner";
            const canEdit = canManage && !isOwner && !(myRole === "admin" && m.role === "admin");
            return (
              <li
                key={m.user_id}
                className="px-6 py-3 flex flex-wrap items-center justify-between gap-3"
                data-testid={`member-row-${m.user_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">
                    {m.name || m.email} {isSelf && <span className="text-xs text-zinc-400">{t("members.you_suffix")}</span>}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <RoleBadge role={m.role} />
                  {canEdit && (
                    <select
                      value={m.role}
                      onChange={(e) => handleChangeRole(m.user_id, e.target.value)}
                      data-testid={`member-role-select-${m.user_id}`}
                      className="text-xs border border-zinc-300 rounded-md px-1.5 py-1 bg-white"
                    >
                      {canInviteAdmin && <option value="admin">admin</option>}
                      <option value="member">member</option>
                      <option value="viewer">viewer</option>
                    </select>
                  )}
                  {(canEdit || isSelf) && !isOwner && (
                    <button
                      onClick={() => handleRemoveMember(m.user_id)}
                      data-testid={`member-remove-${m.user_id}`}
                      title={isSelf ? t("members.leave_tooltip") : t("members.remove_tooltip")}
                      className="w-7 h-7 grid place-items-center rounded hover:bg-rose-50 text-rose-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Invite + pending invitations */}
      {canManage && (
        <section className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            <h2 className="text-base font-semibold text-zinc-900 mb-3">{t("invite.title")}</h2>
            <form onSubmit={handleInvite} className="space-y-3">
              <input
                type="email"
                placeholder={t("invite.email_placeholder")}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                data-testid="invite-email-input"
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                data-testid="invite-role-select"
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                {canInviteAdmin && <option value="admin">{t("invite.role_admin")}</option>}
                <option value="member">{t("invite.role_member")}</option>
                <option value="viewer">{t("invite.role_viewer")}</option>
              </select>
              <button
                type="submit"
                disabled={inviting || !inviteEmail.trim()}
                data-testid="invite-submit-button"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-zinc-950 text-white text-sm font-medium disabled:opacity-50"
              >
                <Mail size={15} /> {inviting ? t("invite.submitting") : t("invite.submit")}
              </button>
            </form>
            <p className="text-xs text-zinc-500 mt-3">
              {t("invite.hint")}
            </p>
          </div>

          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            <h2 className="text-base font-semibold text-zinc-900 mb-3">{t("invitations.title")}</h2>
            {invitations.length === 0 ? (
              <p className="text-sm text-zinc-500" data-testid="invitations-empty">{t("invitations.empty")}</p>
            ) : (
              <ul className="space-y-2" data-testid="invitations-list">
                {invitations.map((inv) => (
                  <li
                    key={inv.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-zinc-200"
                    data-testid={`invitation-row-${inv.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-900 truncate">{inv.email}</p>
                      <p className="text-[11px] text-zinc-500">{t("invitations.role_prefix")}{inv.role}</p>
                    </div>
                    <button
                      onClick={() => handleRevoke(inv.id)}
                      data-testid={`invitation-revoke-${inv.id}`}
                      className="text-xs text-rose-600 hover:underline"
                    >
                      {t("invitations.revoke")}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
